import {
  addMarkerSelector,
  firstAreaAtom,
  isMenuOpenAtom,
  mapRefAtom,
  selectedTimeAtom,
  setSelectedAreaSelector,
  transitioningAtom,
  viewStateAtom,
} from "@/api/atoms";
import { lastReported } from "@/config";
import { useDetectOutsideClick } from "@/hooks/useDetectOutsideClick";
import { UilSearch } from "@iconscout/react-unicons";
import type { SearchBoxRetrieveResponse } from "@mapbox/search-js-core";
import { SearchBox } from "@mapbox/search-js-react";
import { useMediaQuery } from "@react-hookz/web";
import axios from "axios";
import { useAtomValue, useSetAtom } from "jotai";
import { useRef, useState } from "react";

const LayerGeocoder = ({
  placeholder,
  isFirst,
}: {
  placeholder: string;
  isFirst?: boolean;
}) => {
  const ref = useRef<HTMLInputElement | null>(null);
  const [clicked, setClicked] = useDetectOutsideClick(ref, false);
  const [address, setAddress] = useState("");
  const isMobileDevice = useMediaQuery("(max-device-width: 480px)");

  const mapRef = useAtomValue(mapRefAtom);
  const viewState = useAtomValue(viewStateAtom);
  const selectedTime = useAtomValue(selectedTimeAtom);
  const { zoom } = useAtomValue(viewStateAtom);
  const firstArea = useAtomValue(firstAreaAtom);

  const setSelectedArea = useSetAtom(setSelectedAreaSelector);
  const addMarker = useSetAtom(addMarkerSelector);
  const setTransitioning = useSetAtom(transitioningAtom);
  const setIsMenuOpen = useSetAtom(isMenuOpenAtom);

  const onRetrieve = async (result: SearchBoxRetrieveResponse) => {
    setTransitioning(true);
    const placeName = result.features[0].properties.full_address;
    const coordinates = result.features[0].geometry.coordinates;

    const res = await axios.get(
      `${import.meta.env.VITE_API_URL}/thefts/count/2?lat=${
        coordinates[1]
      }&lng=${coordinates[0]}&start=${
        lastReported[selectedTime].start
      }&end=${lastReported[selectedTime].end}`,
    );
    const { yearCounts, lastReportedMonthCount } = await res.data;

    const newMarker = {
      lng: coordinates[0],
      lat: coordinates[1],
      location: placeName,
      counts: {
        yearCounts,
        lastReportedMonthCount,
      },
      isSearch: true,
      isFirst,
    };
    const duration = Math.abs(viewState.zoom - zoom) * 0.3 * 1000;

    mapRef?.flyTo({
      center: [coordinates[0], coordinates[1]],
      zoom: (firstArea && zoom) || 16,
      duration,
    });

    setSelectedArea({ name: placeName, isSearch: true });
    addMarker(newMarker);
    setAddress(newMarker?.location ?? "");
    setIsMenuOpen(false);

    setTimeout(() => {
      setTransitioning(false);
    }, duration);
  };

  const handleCLick = () => setClicked(true);

  return (
    <div ref={ref} className="relative" onClick={handleCLick}>
      <UilSearch className="absolute left-2.5 top-1/2 z-10 -translate-y-1/2" size={20} />
      {!clicked && !address && (
        <span className="absolute left-12 top-1/2 -translate-y-1/2 text-xs text-gray-900">
          Search an address or area
        </span>
      )}
      <SearchBox
        onRetrieve={onRetrieve}
        accessToken={import.meta.env.VITE_MAPBOX_API_KEY}
        popoverOptions={{
          flip: isMobileDevice,
        }}
        placeholder={placeholder}
        onChange={setAddress}
        value={address}
        options={{
          language: "en",
          country: "gb",
          types: new Set([
            "address",
            "district",
            "region",
            "postcode",
            "place",
            "locality",
            "street",
          ]),
        }}
      />
    </div>
  );
};

export default LayerGeocoder;
